import { createBrowserRouter, RouteObject } from 'react-router-dom';
import { DealerManagementPage, DealerProfilePage } from '../features/dealer';
import { ManufacturerManagementPage, ManufacturerProfilePage } from '../features/manufacturer';
import { MessagingInboxPage } from '../features/messaging';
import { OrderDetailsHeader } from '../features/order';
import { OrderManagementPage } from '../features/order/';
import ManageBuildingProjectsPage from '../features/order/manage-building-projects-page';
import { PaintDataManagementPage } from '../features/paint-data';
import {
  BOMSpecificationsPage,
  BundleCostManagementDashboardPage,
  ProductPriceSheetPage,
  ProductPricingCalculatorPage,
  ProductSizePriceTotalsPage,
} from '../features/price-management';
import OrdersDueReport from '../features/reports/orders-due-report';
import OrdersPastDueReport from '../features/reports/orders-past-due-report';
import ProductsReport from '../features/reports/products-report-page';
import TotalFeetPerMonthReport from '../features/reports/total-feet-per-month-report';
import TotalFeetPerWeekReport from '../features/reports/total-feet-per-week-report';
import WeeklyFeetPerDayReport from '../features/reports/weekly-feet-per-day-report';
import { SettingsPage } from '../features/settings';
import { TechnicianManagementPage, TechnicianProfilePage } from '../features/technician';
import { MyProfilePage, UserManagementPage, UserProfilePage } from '../features/user';
import { WorkOrderTravelerPage } from '../features/work-order-traveler';
import DashboardRoute from './dashboard-route';
import HomeRoute from './home-route';
import Layout from './layout';
import ProtectedRoute from './protected-route';

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <HomeRoute />
          </ProtectedRoute>
        ),
      },
      {
        path: 'user-management',
        element: (
          <ProtectedRoute requiresUserAccess>
            <UserManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'my-profile',
        element: (
          <ProtectedRoute>
            <MyProfilePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'user-profile/:id?',
        element: (
          <ProtectedRoute requiresUserAccess>
            <UserProfilePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'manufacturers-management',
        element: (
          <ProtectedRoute requiresManufacturerAccess>
            <ManufacturerManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'manufacturer-profile/:id?',
        element: (
          <ProtectedRoute requiresManufacturerAccess>
            <ManufacturerProfilePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'dealer-management',
        element: (
          <ProtectedRoute requiresDealerAccess>
            <DealerManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'dealer-profile/:id?',
        element: (
          <ProtectedRoute requiresDealerAccess>
            <DealerProfilePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'technician-management',
        element: (
          <ProtectedRoute requiresTechnicianAccess>
            <TechnicianManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'technician-profile/:id?',
        element: (
          <ProtectedRoute requiresTechnicianAccess>
            <TechnicianProfilePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <DashboardRoute />
          </ProtectedRoute>
        ),
      },
      {
        path: 'order-management',
        element: (
          <ProtectedRoute requiresOrderAccess>
            <OrderManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'order-details/:id?',
        element: (
          <ProtectedRoute requiresOrderAccess>
            <OrderDetailsHeader />
          </ProtectedRoute>
        ),
      },
      {
        path: 'order-details/:id/manage-buildings',
        element: (
          <ProtectedRoute requiresOrderAccess>
            <ManageBuildingProjectsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'order-details/:id/traveler',
        element: (
          <ProtectedRoute requiresOrderAccess>
            <WorkOrderTravelerPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'paint-data-management',
        element: (
          <ProtectedRoute>
            <PaintDataManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'product-pricing-calculator',
        element: (
          <ProtectedRoute>
            <BundleCostManagementDashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'bundle-cost-calculator',
        element: (
          <ProtectedRoute>
            <ProductPricingCalculatorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'bundle-cost-management',
        element: (
          <ProtectedRoute>
            <BundleCostManagementDashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'product-price-sheet',
        element: (
          <ProtectedRoute>
            <ProductPriceSheetPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'product-size-price-totals',
        element: (
          <ProtectedRoute>
            <ProductSizePriceTotalsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'bom-specifications',
        element: (
          <ProtectedRoute>
            <BOMSpecificationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'messaging',
        element: (
          <ProtectedRoute>
            <MessagingInboxPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/products',
        element: (
          <ProtectedRoute requiresReportAccess>
            <ProductsReport />
          </ProtectedRoute>
        ),
      },
      {
        // Intentionally uses plain <ProtectedRoute> (not requiresReportAccess) —
        // Dealers are authorized by the backend and see only their own orders.
        // The production feet reports (weekly/monthly) are internal manufacturing
        // data not relevant to Dealers and use requiresReportAccess.
        path: 'reports/orders-due',
        element: (
          <ProtectedRoute>
            <OrdersDueReport />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/orders-past-due',
        element: (
          <ProtectedRoute>
            <OrdersPastDueReport />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/weekly-feet-per-day',
        element: (
          <ProtectedRoute requiresReportAccess>
            <WeeklyFeetPerDayReport />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/total-feet-per-week',
        element: (
          <ProtectedRoute requiresReportAccess>
            <TotalFeetPerWeekReport />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/total-feet-per-month',
        element: (
          <ProtectedRoute requiresReportAccess>
            <TotalFeetPerMonthReport />
          </ProtectedRoute>
        ),
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
